<template>
    <div class="flex flex-col gap-6 fs-mask" :class="preview ? 'mt-4' : 'p-5 sm:p-6'">
        <p class="body-small text-ink-muted empty:sr-only" role="status">{{ isFetching ? t("chat.teamPerspective.resultsLoading") : "" }}</p>
        <div v-if="error" class="flex flex-col items-start gap-3">
            <p role="alert" class="body-small text-ink">{{ t("chat.teamPerspective.resultsError") }}</p>
            <BaseButton theme="neutral" size="sm" :disabled="isFetching" @click="execute()">{{ t("common.tryAgain") }}</BaseButton>
        </div>
        <template v-else-if="report && !isFetching">
            <p v-if="!dimensions.length" class="body text-ink-muted">{{ t("teamPerspective.empty") }}</p>
            <section
                v-for="dimension in dimensions"
                :key="dimension.id"
                class="flex flex-col gap-4"
                :class="{ 'border-t border-border pt-5': !preview }"
                :aria-label="dimension.title">
                <h3 v-if="!preview" class="body-semibold text-ink">{{ dimension.title }}</h3>
                <MarkdownText v-if="!preview" :text="dimension.guidance.overview" class="body-small text-ink" />
                <TeamScoreDistribution
                    :scores="dimension.scores"
                    :members="report.members"
                    :left-label="dimension.left_label"
                    :right-label="dimension.right_label"
                    :viewer-id="report.viewer_id" />
                <p v-if="preview && interpretation" class="body-small text-ink">{{ interpretation }}</p>
                <BaseAccordion
                    v-if="!preview && dimension.lenses.length"
                    :accordion-ids="['lenses']"
                    trigger-class="px-0 py-3 text-ink"
                    @update:model-value="lensesChanged($event, dimension.id)">
                    <template #trigger-lenses
                        ><span class="body-small-semibold">{{ t("teamPage.breakdown") }}</span></template
                    >
                    <template #content-lenses>
                        <div class="flex flex-col gap-5">
                            <section v-for="lens in dimension.lenses" :key="lens.id" class="flex flex-col gap-3">
                                <h4 class="body-small-semibold text-ink">{{ t("chat.teamPerspective.lensTitle", { left: lens.left_label, right: lens.right_label }) }}</h4>
                                <TeamScoreDistribution
                                    :scores="lens.scores"
                                    :members="report.members"
                                    :left-label="lens.left_label"
                                    :right-label="lens.right_label"
                                    :viewer-id="report.viewer_id" />
                            </section>
                        </div>
                    </template>
                </BaseAccordion>
            </section>
            <p v-if="!preview" class="details text-ink-muted">{{ t("teamPerspective.sharing") }}</p>
        </template>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { TeamPerspectivePreview, TeamPerspectiveReport } from "~vue/api/generated/team-perspective";
import BaseAccordion from "~vue/components/BaseAccordion.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import TeamScoreDistribution from "~vue/Perspective/TeamScoreDistribution.vue";
import { TEAM_INTERACTION_SOURCE } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onBeforeUnmount, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ url: string; preview?: boolean; focus?: TeamPerspectivePreview | null }>();
const { t } = useI18n();
const { data: report, error, isFetching, execute, abort } = useFetch(props.url, { immediate: false }).post({}).json<TeamPerspectiveReport>();
let viewedTeamId: string | undefined;
watch(
    () => (report.value?.is_available && !isFetching.value && !error.value ? report.value.id : undefined),
    (teamId) => {
        if (teamId && teamId !== viewedTeamId) {
            viewedTeamId = teamId;
            logUserInteraction(AmplitudeEventNames.TEAM_REPORT_VIEWED, {
                source: TEAM_INTERACTION_SOURCE.CHAT_TOOL,
                team_id: teamId,
                report_view: props.preview ? "preview" : "full",
            });
        }
    },
    { flush: "post" },
);
function lensesChanged(value: string[] | string | undefined, dimension: string) {
    logUserInteraction(AmplitudeEventNames.TEAM_REPORT_CONTROL_SELECTED, {
        source: TEAM_INTERACTION_SOURCE.CHAT_TOOL,
        team_id: report.value?.id,
        team_report_control: "lenses",
        report_dimension: dimension,
        expanded: Array.isArray(value) && value.includes("lenses"),
    });
}
const dimensions = computed(() => {
    const available = report.value?.dimensions ?? [];
    if (!props.preview) {
        return available;
    }
    if (props.focus) {
        const [dimensionId, lensId] = props.focus.evidence_id.split(":");
        const dimension = available.find((item) => item.id === dimensionId);
        if (!dimension) {
            return [];
        }
        if (!lensId) {
            return [dimension];
        }
        const lens = dimension.lenses.find((item) => item.id === lensId);
        return lens ? [{ ...dimension, ...lens, title: `${lens.left_label} / ${lens.right_label}`, lenses: [] }] : [];
    }
    const structure = available.find((dimension) => dimension.id === "JP") ?? available[0];
    return structure ? [structure] : [];
});
const interpretation = computed(() => {
    const focus = props.focus;
    const scores = dimensions.value[0]?.scores;
    if (!focus || !scores || scores.length !== focus.scores.length) {
        return "";
    }
    const current = new Map(scores.map((score) => [score.member_id, score.score]));
    return focus.scores.every((score) => current.get(score.member_id) === score.score) ? focus.interpretation : "";
});
onMounted(() => execute());
onBeforeUnmount(abort);
</script>
